import { io, Socket, ManagerOptions, SocketOptions } from 'socket.io-client';
import axiosInstance from 'api/axios';

const isDev =
  process.env.NODE_ENV === 'development' ||
  (typeof window !== 'undefined' && window.location.hostname.includes('iplatformapp.com'));
const devLog = (...args: any[]) => {
  if (isDev) console.log(...args);
};
const devError = (...args: any[]) => {
  if (isDev) console.error(...args);
};

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

async function getSocketToken(baseUrl?: string, retries = 1): Promise<string | null> {
  try {
    const response = await axiosInstance.get('/client/auth/socket-token', {
      baseURL: baseUrl,
    });
    return response.data?.data?.ticket;
  } catch (error) {
    devError(`SocketService: Failed to fetch socket token. Retries left: ${retries}`, error);

    if (retries > 0) {
      await delay(1500);
      return getSocketToken(baseUrl, retries - 1);
    }

    return null;
  }
}

class SocketService {
  private socket: Socket | null = null;
  private readonly baseUrl: string = process.env.REACT_APP_API_URL || '';
  private isRefreshingToken = false;
  private refreshAttempts = 0;

  private connectPromise: Promise<Socket> | null = null;

  public async connect(): Promise<Socket> {
    if (this.socket?.connected) {
      return this.socket;
    }

    if (this.connectPromise) {
      return this.connectPromise;
    }

    this.connectPromise = (async () => {
      try {
        const ticket = await getSocketToken(this.baseUrl);

        if (this.socket) {
          // Reuse existing socket instance to preserve React component listeners!
          if (ticket) {
            this.socket.auth = { ...(this.socket.auth as any), ticket };
          }
          this.socket.connect();
          return this.socket;
        }

        const socketOptions: Partial<ManagerOptions & SocketOptions> = {
          path: '/socket/',
          transports: ['websocket'],
          withCredentials: true,
          reconnection: true,
        };

        if (ticket) {
          socketOptions.auth = { ticket };
        }

        this.socket = io(this.baseUrl, socketOptions);

        this.socket.on('connect', () => {
          devLog('Socket connected');
          this.refreshAttempts = 0;
        });

        this.socket.on('connect_error', async (error: Error & { data?: { code?: string } }) => {
          devLog(error, 'connect_error');

          const isExpired =
            error.data?.code === 'SOCKET_TICKET_EXPIRED' ||
            error.message?.includes('invalid or expired socket ticket') ||
            error.message?.includes('SOCKET_TICKET_EXPIRED');

          if (isExpired) {
            if (this.isRefreshingToken) {
              devLog('Socket ticket refresh already in progress. Skipping...');
              return;
            }

            this.isRefreshingToken = true;

            try {
              devLog('Socket ticket expired. Refreshing socket token...');
              const newTicket = await getSocketToken(this.baseUrl);

              if (newTicket && this.socket) {
                this.socket.disconnect();

                this.socket.auth = {
                  ...(this.socket.auth as any),
                  ticket: newTicket,
                };

                this.socket.connect();
              } else {
                const waitTime = Math.min(2000 * Math.pow(2, this.refreshAttempts), 60000);
                this.refreshAttempts++;

                devError(
                  `SocketService: Failed to retrieve new socket ticket after expiration. Retrying connection in ${waitTime}ms (Attempt ${this.refreshAttempts})...`,
                );

                setTimeout(() => {
                  if (this.socket && !this.socket.connected) {
                    this.socket.connect();
                  }
                }, waitTime);
              }
            } finally {
              this.isRefreshingToken = false;
            }
          }
        });

        this.socket.on('disconnect', (reason) => {
          devLog('Socket disconnected:', reason);
        });

        return this.socket;
      } finally {
        this.connectPromise = null;
      }
    })();

    return this.connectPromise;
  }

  public async forceReconnect(): Promise<void> {
    devLog('Forcing socket reconnect...');
    this.disconnect();
    this.refreshAttempts = 0;
    this.isRefreshingToken = false;
    await this.connect();
  }

  public disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      // DO NOT set this.socket = null, so we can reuse the same instance and preserve listeners!
    }
  }

  public getSocket(): Socket | null {
    return this.socket;
  }

  public on(event: string, callback: (...args: any[]) => void): void {
    this.socket?.on(event, callback);
  }

  public off(event: string, callback?: (...args: any[]) => void): void {
    this.socket?.off(event, callback);
  }

  public emit(event: string, data: any): void {
    this.socket?.emit(event, data);
  }
}

export const socketService = new SocketService();
